/**
 * Tesorería del Portal Condominio: ingresos reales por mes (`get_condominio_tesoreria`,
 * migración 20261016000000). Egresos y presupuesto no tienen tabla en la base: la pantalla lo
 * dice en lugar de mostrar datos de ejemplo.
 *
 * Amenidades (#1522): la RPC suma aparte lo aplicado a cargos de reserva (concepto 14) en
 * `amenidades`. Mientras la migración no llegue al ambiente el campo no viene (`null`) y la
 * columna no se pinta.
 */
import { centavos } from "./unidades";

export interface MesTesoreria {
  /** Primer día del mes (YYYY-MM-DD). */
  mes: string;
  recibido: number;
  pagos: number;
  mantenimiento: number;
  fondo: number;
  multas: number;
  /** Aplicado a reservas de amenidades (concepto 14); `null` si la RPC aún no lo manda. */
  amenidades: number | null;
  sin_aplicar: number;
  aplicado_de_mas: number;
}

/** PostgREST devuelve `numeric` como texto: se convierte una vez al llegar. */
export function normalizarMesTesoreria(f: Record<string, unknown>): MesTesoreria {
  const n = (k: string) => Number(f[k]) || 0;
  return {
    mes: String(f.mes ?? "").slice(0, 10),
    recibido: n("recibido"),
    pagos: n("pagos"),
    mantenimiento: n("mantenimiento"),
    fondo: n("fondo"),
    multas: n("multas"),
    amenidades: f.amenidades == null ? null : n("amenidades"),
    sin_aplicar: n("sin_aplicar"),
    aplicado_de_mas: n("aplicado_de_mas"),
  };
}

export type TotalesTesoreria = Omit<MesTesoreria, "mes">;

/** Suma del periodo en centavos (sin arrastre de flotantes). */
export function totalesTesoreria(meses: MesTesoreria[]): TotalesTesoreria {
  const c = { recibido: 0, pagos: 0, mantenimiento: 0, fondo: 0, multas: 0, amenidades: 0, sin_aplicar: 0, aplicado_de_mas: 0 };
  let conAmenidades = false;
  for (const m of meses) {
    if (m.amenidades != null) { conAmenidades = true; c.amenidades += centavos(m.amenidades); }
    c.recibido += centavos(m.recibido);
    c.pagos += m.pagos;
    c.mantenimiento += centavos(m.mantenimiento);
    c.fondo += centavos(m.fondo);
    c.multas += centavos(m.multas);
    c.sin_aplicar += centavos(m.sin_aplicar);
    c.aplicado_de_mas += centavos(m.aplicado_de_mas);
  }
  return {
    recibido: c.recibido / 100,
    pagos: c.pagos,
    mantenimiento: c.mantenimiento / 100,
    fondo: c.fondo / 100,
    multas: c.multas / 100,
    amenidades: conAmenidades ? c.amenidades / 100 : null,
    sin_aplicar: c.sin_aplicar / 100,
    aplicado_de_mas: c.aplicado_de_mas / 100,
  };
}

/**
 * ¿Cuadra el mes? recibido = mantenimiento + fondo + multas + amenidades + sin aplicar - aplicado
 * de más. La RPC lo garantiza; el front lo comprueba para no pintar un desglose que no suma.
 */
export const mesCuadra = (m: Pick<MesTesoreria, "recibido" | "mantenimiento" | "fondo" | "multas" | "sin_aplicar" | "aplicado_de_mas"> & { amenidades?: number | null }): boolean =>
  centavos(m.recibido)
    === centavos(m.mantenimiento) + centavos(m.fondo) + centavos(m.multas) + centavos(m.amenidades ?? 0) + centavos(m.sin_aplicar) - centavos(m.aplicado_de_mas);

/** ¿Algún mes trae lo de amenidades? (si no, la RPC del ambiente es anterior a #1522). */
export const conAmenidades = (meses: Pick<MesTesoreria, "amenidades">[]): boolean => meses.some((m) => m.amenidades != null);

export const MESES_TESORERIA = [
  { value: "6", label: "Últimos 6 meses" },
  { value: "12", label: "Últimos 12 meses" },
  { value: "24", label: "Últimos 24 meses" },
  { value: "36", label: "Últimos 36 meses" },
] as const;

export const esMesesTesoreria = (v: string): boolean => MESES_TESORERIA.some((m) => m.value === v);
